import { useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { QueryError } from '@/components/QueryError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { gnf } from '@/lib/invoices';

// Abonnement SaaS du tenant (billing-service, billing.read) — à ne pas confondre avec les factures clients
// (/customer-invoices, invoices.read). Montants Decimal sérialisés en chaînes.
interface Subscription {
  planId: string;
  status: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'SUSPENDED';
  currentPeriodStart: string; currentPeriodEnd: string;
  cancelAt: string | null; trialEnd: string | null;
}
interface Plan {
  id: string; name: string; maxVehicles: number; pricePerVehicle: number; basePrice: number;
  currency: string; features: string[];
}
interface SaasInvoice {
  id: string; number: string; status: string; periodStart: string; periodEnd: string;
  total: string; paidAmount: string; currency: string; dueAt: string; paidAt: string | null;
}
interface SaasPayment {
  id: string; amount: string; currency: string; status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'REFUNDED';
  type: string; reference: string | null; paidAt: string | null; createdAt: string;
}
interface PaymentMethod {
  id: string; type: string; isDefault: boolean; last4: string | null; brand: string | null;
  expiryMonth: number | null; expiryYear: number | null; holderName: string | null;
}

export const BILLING_PAGE_SIZE = 10;

const SUBSCRIPTION_STATUS: Record<Subscription['status'], { label: string; variant: 'success' | 'secondary' | 'warning' | 'destructive' }> = {
  TRIAL: { label: 'Essai', variant: 'secondary' },
  ACTIVE: { label: 'Actif', variant: 'success' },
  PAST_DUE: { label: 'Paiement en retard', variant: 'warning' },
  CANCELLED: { label: 'Résilié', variant: 'destructive' },
  SUSPENDED: { label: 'Suspendu', variant: 'destructive' },
};

const INVOICE_STATUS: Record<string, string> = {
  DRAFT: 'Brouillon', ISSUED: 'Émise', PAID: 'Payée', PARTIALLY_PAID: 'Partiellement payée',
  OVERDUE: 'En retard', CANCELLED: 'Annulée', REFUNDED: 'Remboursée',
};

const PAYMENT_STATUS: Record<SaasPayment['status'], string> = {
  PENDING: 'En attente', SUCCEEDED: 'Réussi', FAILED: 'Échoué', REFUNDED: 'Remboursé',
};

const METHOD_LABELS: Record<string, string> = {
  CARD: 'Carte', BANK_TRANSFER: 'Virement', ORANGE_MONEY: 'Orange Money', MTN_MOMO: 'MTN MoMo', WAVE: 'Wave',
};

const dateFr = (d: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

/** Pagination par offset : page suivante tant que la page courante est pleine. */
const usePagedList = <T,>(key: string, url: string, select: (data: unknown) => T[]) => {
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: [key, offset],
    queryFn: async () => select((await api.get(url, { params: { limit: BILLING_PAGE_SIZE, offset } })).data),
    placeholderData: keepPreviousData,
  });
  return {
    query,
    pager: (
      <div className="flex items-center justify-end gap-2 pt-2">
        <span className="text-xs text-muted-foreground">Page {offset / BILLING_PAGE_SIZE + 1}</span>
        <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(offset - BILLING_PAGE_SIZE)}>Précédent</Button>
        <Button size="sm" variant="outline" disabled={(query.data?.length ?? 0) < BILLING_PAGE_SIZE}
          onClick={() => setOffset(offset + BILLING_PAGE_SIZE)}>Suivant</Button>
      </div>
    ),
  };
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Card>
    <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

/** Onglet « Abonnement » : lecture seule (billing.read) ; le changement de plan passe par LOGISTECH-LGT. */
export function BillingTab() {
  const subscriptionQuery = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: async () => (await api.get<Subscription>('/billing/subscription')).data,
  });
  const subscription = subscriptionQuery.data;
  const planQuery = useQuery({
    queryKey: ['billing-plan', subscription?.planId],
    queryFn: async () => (await api.get<Plan>(`/billing/plans/${subscription!.planId}`)).data,
    enabled: !!subscription,
  });
  const plan = planQuery.data;

  // GET /billing/invoices : tableau ; GET /billing/invoices/payments : { data, pagination }
  const invoices = usePagedList('billing-invoices', '/billing/invoices', (d) => d as SaasInvoice[]);
  const payments = usePagedList('billing-payments', '/billing/invoices/payments', (d) => (d as { data: SaasPayment[] }).data);
  const methodsQuery = useQuery({
    queryKey: ['billing-payment-methods'],
    queryFn: async () => (await api.get<PaymentMethod[]>('/billing/payment-methods')).data,
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Plan actuel</CardTitle>
          <TooltipProvider>
            <Tooltip>
              {/* span : un bouton désactivé n'émet pas d'événements pointer, le tooltip ne s'ouvrirait pas */}
              <TooltipTrigger asChild>
                <span tabIndex={0}><Button variant="outline" disabled>Changer de plan</Button></span>
              </TooltipTrigger>
              <TooltipContent>Contactez LOGISTECH-LGT</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardHeader>
        <CardContent>
          <QueryError query={[subscriptionQuery, planQuery]} />
          {subscription && (
            <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
              <dt className="text-muted-foreground">Plan</dt>
              <dd className="flex items-center gap-2">
                <span className="font-medium">{plan?.name ?? subscription.planId}</span>
                <Badge variant={SUBSCRIPTION_STATUS[subscription.status].variant}>{SUBSCRIPTION_STATUS[subscription.status].label}</Badge>
              </dd>
              {plan && (
                <>
                  <dt className="text-muted-foreground">Tarif</dt>
                  <dd>{gnf(plan.basePrice, plan.currency)} / mois + {gnf(plan.pricePerVehicle, plan.currency)} par véhicule</dd>
                  <dt className="text-muted-foreground">Véhicules inclus</dt>
                  <dd>{plan.maxVehicles >= 9999 ? 'Illimité' : `Jusqu’à ${plan.maxVehicles}`}</dd>
                  <dt className="text-muted-foreground">Fonctionnalités</dt>
                  <dd>{plan.features.join(' · ')}</dd>
                </>
              )}
              <dt className="text-muted-foreground">{subscription.status === 'TRIAL' ? 'Fin de l’essai' : 'Période en cours'}</dt>
              <dd>
                {subscription.status === 'TRIAL'
                  ? dateFr(subscription.trialEnd ?? subscription.currentPeriodEnd)
                  : `${dateFr(subscription.currentPeriodStart)} → ${dateFr(subscription.currentPeriodEnd)}`}
              </dd>
              {subscription.cancelAt && (
                <>
                  <dt className="text-muted-foreground">Résiliation prévue</dt>
                  <dd>{dateFr(subscription.cancelAt)}</dd>
                </>
              )}
            </dl>
          )}
          <p className="mt-3 text-xs text-muted-foreground">Pour changer de plan, contactez LOGISTECH-LGT.</p>
        </CardContent>
      </Card>

      <Section title="Factures d’abonnement">
        <QueryError query={invoices.query} />
        {invoices.query.data?.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune facture</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N°</TableHead><TableHead>Période</TableHead><TableHead>Échéance</TableHead>
                <TableHead className="text-right">Montant</TableHead><TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.query.data?.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-mono">{inv.number}</TableCell>
                  <TableCell>{dateFr(inv.periodStart)} → {dateFr(inv.periodEnd)}</TableCell>
                  <TableCell>{dateFr(inv.dueAt)}</TableCell>
                  <TableCell className="text-right">{gnf(Number(inv.total), inv.currency)}</TableCell>
                  <TableCell>{INVOICE_STATUS[inv.status] ?? inv.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {invoices.pager}
      </Section>

      <Section title="Paiements">
        <QueryError query={payments.query} />
        {payments.query.data?.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun paiement</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Moyen</TableHead><TableHead>Référence</TableHead>
                <TableHead className="text-right">Montant</TableHead><TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.query.data?.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{dateFr(p.paidAt ?? p.createdAt)}</TableCell>
                  <TableCell>{METHOD_LABELS[p.type] ?? p.type}</TableCell>
                  <TableCell className="font-mono text-xs">{p.reference ?? '—'}</TableCell>
                  <TableCell className="text-right">{gnf(Number(p.amount), p.currency)}</TableCell>
                  <TableCell>{PAYMENT_STATUS[p.status]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {payments.pager}
      </Section>

      <Section title="Moyens de paiement">
        <QueryError query={methodsQuery} />
        {methodsQuery.data?.length === 0 && <p className="text-sm text-muted-foreground">Aucun moyen de paiement enregistré</p>}
        <div className="space-y-2">
          {methodsQuery.data?.map((m) => (
            <div key={m.id} className="flex items-center justify-between rounded border p-3 text-sm">
              <span>
                {METHOD_LABELS[m.type] ?? m.type}
                {m.brand && ` ${m.brand}`}
                {m.last4 && <span className="font-mono"> •••• {m.last4}</span>}
                {m.expiryMonth && m.expiryYear && (
                  <span className="text-muted-foreground"> · exp. {String(m.expiryMonth).padStart(2, '0')}/{m.expiryYear}</span>
                )}
              </span>
              {m.isDefault && <Badge variant="secondary">Par défaut</Badge>}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
